import { useState } from 'react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { clsx } from 'clsx';
import { ExclamationCircleIcon, LockClosedIcon } from '@heroicons/react/24/outline';

export interface PaymentFormProps {
  amount: number;  // Amount in cents
  platformFee: number;  // Platform fee in cents
  therapistAmount: number;  // Therapist amount in cents
  onPaymentSuccess: () => void;
  onPaymentError: (error: string) => void;
  clientSecret: string;
  isProcessing?: boolean;
}

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: '16px',
      color: '#1f2937',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      '::placeholder': {
        color: '#9ca3af',
      },
    },
    invalid: {
      color: '#dc2626',
      iconColor: '#dc2626',
    },
  },
  hidePostalCode: false,
};

/**
 * PaymentForm component using Stripe Elements
 * Handles secure payment processing for booking
 */
export function PaymentForm({
  amount,
  platformFee,
  therapistAmount,
  onPaymentSuccess,
  onPaymentError,
  clientSecret,
  isProcessing = false,
}: PaymentFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [cardError, setCardError] = useState<string | null>(null);

  const formatCurrency = (cents: number): string => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsSubmitting(true);
    setCardError(null);

    const cardElement = elements.getElement(CardElement);

    if (!cardElement) {
      setCardError('Card element not found');
      setIsSubmitting(false);
      return;
    }

    try {
      // Confirm the payment with Stripe
      const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElement,
        },
      });

      if (error) {
        setCardError(error.message || 'Payment failed');
        onPaymentError(error.message || 'Payment failed');
        setIsSubmitting(false);
      } else if (paymentIntent && paymentIntent.status === 'succeeded') {
        onPaymentSuccess();
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      setCardError(errorMessage);
      onPaymentError(errorMessage);
      setIsSubmitting(false);
    }
  };

  const handleCardChange = (event: any) => {
    setCardError(event.error ? event.error.message : null);
  };

  const disabled = !stripe || isSubmitting || isProcessing;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Amount Breakdown */}
      <div className="bg-gray-50 rounded-lg p-4 space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Session Fee</span>
          <span className="font-medium text-gray-900">
            {formatCurrency(therapistAmount)}
          </span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Platform Fee</span>
          <span className="font-medium text-gray-900">
            {formatCurrency(platformFee)}
          </span>
        </div>
        <div className="pt-2 border-t border-gray-200">
          <div className="flex justify-between">
            <span className="text-base font-semibold text-gray-900">Total</span>
            <span className="text-base font-semibold text-gray-900">
              {formatCurrency(amount)}
            </span>
          </div>
        </div>
      </div>

      {/* Card Input */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Card Information
        </label>
        <div
          className={clsx(
            'px-4 py-3 border rounded-lg transition-colors',
            cardError
              ? 'border-red-300 bg-red-50'
              : 'border-gray-300 bg-white hover:border-gray-400 focus-within:border-indigo-500 focus-within:ring-1 focus-within:ring-indigo-500'
          )}
        >
          <CardElement
            options={CARD_ELEMENT_OPTIONS}
            onChange={handleCardChange}
          />
        </div>
        {cardError && (
          <div className="mt-2 flex items-center gap-2 text-sm text-red-600">
            <ExclamationCircleIcon className="h-5 w-5" />
            <span>{cardError}</span>
          </div>
        )}
      </div>

      {/* Security Notice */}
      <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-100 rounded-lg">
        <LockClosedIcon className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
        <div className="text-sm text-blue-800">
          <p className="font-medium">Secure Payment</p>
          <p className="mt-1 text-blue-700">
            Your payment information is encrypted and securely processed by Stripe.
            We never store your card details.
          </p>
        </div>
      </div>

      {/* Submit Button */}
      <button
        type="submit"
        disabled={disabled}
        className={clsx(
          'w-full px-6 py-3 text-base font-semibold rounded-lg transition-colors',
          disabled
            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
            : 'bg-indigo-600 text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'
        )}
      >
        {isSubmitting || isProcessing ? (
          <span className="flex items-center justify-center gap-2">
            <svg
              className="animate-spin h-5 w-5 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            Processing Payment...
          </span>
        ) : (
          `Pay ${formatCurrency(amount)}`
        )}
      </button>

      {/* Terms */}
      <p className="text-xs text-gray-500 text-center">
        By confirming payment, you agree to our{' '}
        <a href="/terms" className="text-indigo-600 hover:text-indigo-700 underline">
          Terms of Service
        </a>{' '}
        and{' '}
        <a href="/privacy" className="text-indigo-600 hover:text-indigo-700 underline">
          Privacy Policy
        </a>
        .
      </p>
    </form>
  );
}
